/**
 * Per-route error boundary. Friendly fallback when a page throws —
 * shows the error + a "Try again" button. Wraps the lazy-loaded routes.
 */

import React from "react";
import { AlertOctagon } from "lucide-react";
import BrandGradientHero from "./BrandGradientHero";

interface Props { children: React.ReactNode; }
interface State { error: Error | null; }

export class ErrorBoundary extends React.Component<Props, State> {
  state: State = { error: null };
  static getDerivedStateFromError(error: Error): State { return { error }; }
  componentDidCatch(error: Error) { console.error("[ErrorBoundary]", error); }
  reset = () => this.setState({ error: null });

  render() {
    if (!this.state.error) return this.props.children;

    return (
      <BrandGradientHero variant="contained" intensity="soft" minHeight="60vh">
        <div style={{
          flex: 1, display: "flex", flexDirection: "column",
          alignItems: "center", justifyContent: "center",
          textAlign: "center", padding: "48px 24px", gap: 20,
        }}>
          <div style={{
            width: 60, height: 60,
            borderRadius: "var(--radius-pill)",
            background: "var(--status-danger-bg)",
            color: "var(--status-danger)",
            display: "inline-flex", alignItems: "center", justifyContent: "center",
          }}>
            <AlertOctagon size={28} strokeWidth={1.5} />
          </div>
          <h1 style={{ fontSize: "var(--font-size-3xl)", fontFamily: "var(--font-display)", letterSpacing: "var(--tracking-display-md)" }}>
            Something glitched
          </h1>
          <p style={{ fontSize: "var(--font-size-md)", color: "var(--text-secondary)", maxWidth: 480 }}>
            {this.state.error.message || "An unexpected error occurred."}
          </p>
          <button
            onClick={this.reset}
            style={{
              padding: "10px 20px",
              background: "#1c1c1c", color: "#fcfbf8",
              border: "none", borderRadius: "var(--radius-md)",
              fontSize: "var(--font-size-sm)", fontWeight: 600,
              cursor: "pointer", boxShadow: "var(--shadow-button-inset)",
            }}
          >
            Try again
          </button>
        </div>
      </BrandGradientHero>
    );
  }
}

export default ErrorBoundary;
